<template>
  <main class="bg-gray-50 text-gray-900 relative">
    <section class="max-w-6xl mx-auto px-4 pt-14 pb-16 md:pt-20">
      <!-- Header Section -->
      <div class="text-center mb-8 md:mb-12">
        <div
          class="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl mb-4 md:mb-6"
        >
          <svg
            class="w-8 h-8 md:w-10 md:h-10 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            />
          </svg>
        </div>
        <h1
          class="text-[25px] md:text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-3 md:mb-4"
        >
          Welcome to Educanium
        </h1>
        <p
          v-if="!allStepsCompleted"
          class="text-[16px] md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed"
        >
          Set up your school management system in <b>3</b> simple steps
        </p>
      </div>

      <!-- Steps -->
      <ol
        class="mt-10 md:mt-12 flex flex-col gap-4 md:flex-row md:items-center md:justify-center md:gap-10"
        aria-label="Setup steps"
      >
        <li
          v-for="(step, i) in steps"
          :key="step.id"
          class="flex items-start gap-3 md:items-center"
        >
          <div class="flex items-start gap-3">
            <div
              :class="[
                'h-12 w-12 shrink-0 grid place-items-center rounded-2xl text-sm font-semibold',
                step.id === activeStep
                  ? 'text-white shadow-lg ring-1 ring-white/40 bg-gradient-to-br from-purple-500 to-indigo-500'
                  : isStepDone(step.id)
                  ? 'text-white bg-emerald-500 shadow-lg ring-1 ring-white/40'
                  : 'text-gray-700 bg-gray-200/70 shadow-sm',
              ]"
              :aria-current="step.id === activeStep ? 'step' : undefined"
            >
              {{ step.id }}
            </div>
            <div>
              <p class="font-semibold text-gray-800">{{ step.title }}</p>
              <p class="text-xs text-gray-500 leading-5">{{ step.caption }}</p>
            </div>
          </div>

          <span
            v-if="i < steps.length - 1"
            class="hidden md:block mx-4 text-2xl text-gray-300 select-none"
            aria-hidden="true"
          >
            —
          </span>
        </li>
      </ol>

      <!-- Step content -->
      <section class="mt-2 md:mt-2 px-6 py-10 md:px-14 md:py-14">
        <div class="max-w-3xl mx-auto text-center">
          <!-- Step 1: Sign Up -->
          <template v-if="activeStep === 1">
            <div
              class="relative max-w-md w-full mx-auto p-6 md:p-8 rounded-2xl bg-white/70 backdrop-blur-md shadow-2xl border border-white/40 transition-all duration-300 hover:shadow-indigo-300/30"
            >
              <!-- Step 1: Account Form -->
              <form
                v-if="phase === 'form'"
                @submit.prevent="onSubmitAccount"
                class="grid gap-5 animate-fade-in"
                novalidate
              >
                <div
                  class="grid grid-cols-1 md:grid-cols-[150px_1fr] items-center gap-3"
                >
                  <!-- Full Name -->
                  <label
                    for="name"
                    class="flex items-center text-sm font-semibold text-gray-700"
                  >
                    <User class="w-4 h-4 mr-2 text-gray-500" />
                    Name :
                    <span class="text-red-500 ml-1">*</span>
                  </label>

                  <div class="relative">
                    <User
                      class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                    />
                    <input
                      ref="firstFieldRef"
                      id="name"
                      v-model.trim="form.name"
                      type="text"
                      required
                      autocomplete="name"
                      class="h-11 w-full pl-10 rounded-xl border border-gray-200 bg-white/80 px-4 text-gray-900 placeholder:text-gray-400 shadow-inner focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400 transition-all"
                      placeholder="Jane Doe"
                    />
                  </div>
                </div>

                <!-- Phone -->
                <div
                  class="grid grid-cols-1 md:grid-cols-[150px_1fr] items-center gap-3"
                >
                  <label
                    for="phone"
                    class="flex items-center text-sm font-semibold text-gray-700"
                  >
                    <Phone class="w-4 h-4 mr-2 text-gray-500" />
                    Phone :
                    <span class="text-red-500 ml-1">*</span>
                  </label>
                  <div class="relative">
                    <Phone
                      class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                    />
                    <input
                      id="phone"
                      v-model.trim="form.phone"
                      type="tel"
                      inputmode="tel"
                      required
                      autocomplete="tel"
                      class="h-11 w-full pl-10 rounded-xl border border-gray-200 bg-white/80 px-4 text-gray-900 placeholder:text-gray-400 shadow-inner focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400 transition-all"
                      placeholder="eg... 9876890765"
                    />
                  </div>
                </div>
                <!-- Email (Optional) -->
                <div
                  class="grid grid-cols-1 md:grid-cols-[150px_1fr] items-center gap-3"
                >
                  <label
                    for="email"
                    class="flex items-center text-sm font-semibold text-gray-700"
                  >
                    <Mail class="w-4 h-4 mr-2 text-gray-500" />
                    Email :
                  </label>

                  <div class="relative">
                    <Mail
                      class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                    />
                    <input
                      id="email"
                      v-model.trim="form.email"
                      type="email"
                      autocomplete="email"
                      class="h-11 w-full pl-10 rounded-xl border border-gray-200 bg-white/80 px-4 text-gray-900 placeholder:text-gray-400 shadow-inner focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400 transition-all"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <p
                  v-if="error"
                  class="text-sm text-red-600 bg-red-50 border border-red-200 p-2 rounded-lg"
                >
                  {{ error }}
                </p>
                <p
                  v-if="success"
                  class="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2"
                >
                  {{ success }}
                </p>

                <button
                  type="submit"
                  :disabled="loading"
                  class="mt-3 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2 font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 shadow-md transition-all duration-300 disabled:opacity-60 hover:via-pink-400 hover:from-indigo-400 hover:to-purple-500 ml-[40px] mr-[40px]"
                >
                  <Send
                    v-if="!loading"
                    class="w-5 h-5"
                  />
                  <Loader2
                    v-else
                    class="w-5 h-5 animate-spin"
                  />
                  <span>{{ loading ? "Sending OTP..." : "Send OTP" }}</span>
                </button>
                <!-- 👇 Login link -->
                <div class="text-center mt-4">
                  <RouterLink
                    to="/login"
                    class="text-sm font-medium text-indigo-600 hover:text-purple-600 transition-all"
                  >
                    Already have an account? Login
                  </RouterLink>
                </div>
              </form>

              <!-- Step 2: OTP Verification -->
              <form
                v-else
                @submit.prevent="onVerifyOtp"
                class="grid gap-5 animate-fade-in"
                novalidate
              >
                <h2
                  class="text-2xl font-semibold text-left bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent"
                >
                  <ShieldCheck
                    class="inline-block w-6 h-6 text-indigo-500 mr-2"
                  />
                  Verify Your Phone
                </h2>

                <div class="grid gap-2">
                  <label
                    for="otp"
                    class="text-sm font-medium text-gray-700 text-left"
                  >
                    Enter the 6-digit OTP sent to
                    <span class="font-semibold text-indigo-600">
                      {{ otpSentVia === "email" ? form.email : maskedPhone }}
                    </span>
                  </label>

                  <div class="relative">
                    <KeyRound
                      class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
                    />
                    <input
                      ref="otpInputRef"
                      id="otp"
                      v-model.trim="otp"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      maxlength="6"
                      minlength="6"
                      autocomplete="one-time-code"
                      required
                      class="h-11 w-full pl-10 rounded-xl border border-gray-200 bg-white/80 px-4 text-center text-gray-900 placeholder:text-gray-400 tracking-widest text-lg font-semibold shadow-inner focus:ring-2 focus:ring-indigo-500 transition-all"
                      placeholder="0 0 0 0 0 0"
                    />
                  </div>

                  <p
                    id="otp-help"
                    class="text-xs text-gray-500 text-left"
                  >
                    Didn’t get it?
                    <button
                      type="button"
                      class="underline text-indigo-600 hover:text-indigo-800 font-medium disabled:text-gray-400 transition-all"
                      :disabled="resendDisabled || loading"
                      @click="onResendOtp"
                    >
                      Resend OTP
                    </button>
                    <span
                      v-if="resendDisabled"
                      class="ml-1"
                      >⏳ in {{ countdown }}s</span
                    >
                  </p>
                </div>

                <p
                  v-if="error"
                  class="text-sm text-red-600 bg-red-50 border border-red-200 p-2 rounded-lg"
                >
                  {{ error }}
                </p>
                <p
                  v-if="success"
                  class="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-2"
                >
                  {{ success }}
                </p>

                <div class="flex items-center justify-start gap-3">
                  <button
                    type="button"
                    class="text-sm font-medium text-gray-600 hover:text-indigo-600 transition-all flex items-center gap-1"
                    @click="
                      phase = 'form';
                      error = '';
                      success = '';
                      otp = '';
                      stopCountdown();
                      resendDisabled = false;
                    "
                  >
                    <Edit3 class="w-4 h-4" /> Edit details
                  </button>
                  <button
                    type="submit"
                    :disabled="loading"
                    class="inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 font-semibold text-white bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 shadow-md hover:scale-105 active:scale-95 transition-all duration-200 disabled:opacity-60"
                  >
                    <CheckCircle2
                      v-if="!loading"
                      class="w-5 h-5"
                    />
                    <Loader2
                      v-else
                      class="w-5 h-5 animate-spin"
                    />
                    <span>{{
                      loading ? "Verifying…" : "Verify & Continue"
                    }}</span>
                  </button>
                </div>
              </form>
            </div>
          </template>
        </div>
      </section>
    </section>
  </main>
</template>
<script setup>
  import { ref, onBeforeUnmount, computed, watch, nextTick } from "vue";
  import axios from "axios";
  import {
    User,
    Phone,
    Send,
    Loader2,
    ShieldCheck,
    KeyRound,
    CheckCircle2,
    Edit3,
    Mail,
  } from "lucide-vue-next";

  import { useRouter } from "vue-router";
  import { useStore } from "vuex";
  import jwt_decode from "jwt-decode";
  // Base URL (env or fallback)
  const baseURL = process.env.VUE_APP_BASE_URL || "http://localhost:5000";

  const router = useRouter();
  const store = useStore();

  const sendOtpUrl = `${baseURL}/api/auth/signup/send-otp`;
  const signupUrl = `${baseURL}/api/auth/signup`;

  // Steps with "Sign Up" as Step 1
  const steps = [
    { id: 1, title: "Sign Up", caption: "Create your account" },
    { id: 2, title: "Create School", caption: "Set up your school profile" },
    { id: 3, title: "Add Classes", caption: "Create your first class" },
  ];

  const activeStep = ref(1);

  // Signup state

  const isSignedUp = ref(false);
  const otpSentVia = ref("phone");
  const error = ref("");
  const success = ref("");
  const loading = ref(false);
  const otpInputRef = ref(null);

  const phase = ref("form"); // 'form' | 'otp'
  const otp = ref("");
  const resendDisabled = ref(false);
  const countdown = ref(60);
  let countdownTimer = null;

  const form = ref({
    name: "",
    phone: "",
    email: "",
  });

  const firstFieldRef = ref(null);
  const maskedPhone = computed(() => maskPhone(form.value.phone));

  onBeforeUnmount(() => {
    document.removeEventListener("keydown", onEsc);
    stopCountdown();
  });

  // Utility
  function onEsc(e) {
    if (e.key === "Escape") closeModal();
  }
  function resetMessages() {
    error.value = "";
    success.value = "";
  }

  function normalizePhone(phone) {
    return (phone || "").replace(/\s+/g, "").trim();
  }
  function maskPhone(phone) {
    const p = phone || "";
    if (p.length <= 4) return "****";
    return (
      p.slice(0, Math.max(0, p.length - 4)).replace(/./g, "•") + p.slice(-4)
    );
  }
  function validateAccount() {
    const phone = normalizePhone(form.value.phone);

    if (!form.value.name || !form.value.phone) {
      error.value = "Please fill out all fields.";
      return false;
    }
    if (form.value.email && !/^\S+@\S+\.\S+$/.test(form.value.email)) {
      error.value = "Please enter a valid email address.";
      return false;
    }

    // Check if phone is exactly 10 digits
    if (!/^\d{10}$/.test(phone)) {
      error.value = "Please enter a valid 10-digit phone number.";
      return false;
    }

    return true;
  }

  // OTP countdown
  function startCountdown() {
    resendDisabled.value = true;
    countdown.value = 60;
    stopCountdown();
    countdownTimer = setInterval(() => {
      countdown.value -= 1;
      if (countdown.value <= 0) {
        stopCountdown();
        resendDisabled.value = false;
      }
    }, 1000);
  }
  function stopCountdown() {
    if (countdownTimer) {
      clearInterval(countdownTimer);
      countdownTimer = null;
    }
  }

  // API: Signup flow
  async function onSubmitAccount() {
    resetMessages();
    if (!validateAccount()) return;

    loading.value = true;
    try {
      const payload = {
        name: form.value.name,
        phone: normalizePhone(form.value.phone),
        email: form.value.email || undefined,
      };
      const res = await axios.post(sendOtpUrl, payload);
      success.value = res.data?.message || "OTP sent successfully.";

      if (res.data?.message?.toLowerCase().includes("email")) {
        otpSentVia.value = "email";
      } else {
        otpSentVia.value = "phone";
      }

      phase.value = "otp";

      startCountdown();
      await nextTick();
      otpInputRef.value?.focus();
    } catch (err) {
      console.error("OTP Error:", err.response?.data);
      error.value =
        err.response?.data?.error || // backend error field
        err.response?.data?.message || // fallback
        "🚨 Server error while sending OTP. Please try again later.";
    } finally {
      loading.value = false;
    }
  }

  async function onResendOtp() {
    resetMessages();
    loading.value = true;
    try {
      const payload = {
        name: form.value.name,
        phone: normalizePhone(form.value.phone),
        email: form.value.email || undefined,
      };
      const res = await axios.post(sendOtpUrl, payload);
      success.value = res.data?.message || "OTP re-sent.";

      if (res.data?.message?.toLowerCase().includes("email")) {
        otpSentVia.value = "email";
      } else {
        otpSentVia.value = "phone";
      }

      startCountdown();
      await nextTick();
      otpInputRef.value?.focus();
    } catch (err) {
      console.error("Resend OTP Error:", err.response?.data);
      error.value =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "🚨 Server error while resending OTP. Please try again later.";
    } finally {
      loading.value = false;
    }
  }
  async function onVerifyOtp() {
    resetMessages();
    if (!otp.value || otp.value.length !== 6) {
      error.value = "Please enter the 6-digit OTP.";
      return;
    }

    loading.value = true;
    try {
      const payload = {
        phone: normalizePhone(form.value.phone),
        otp: otp.value,
        name: form.value.name || undefined,
        email: form.value.email || undefined,
      };
      const res = await axios.post(signupUrl, payload);
      const data = res.data;

      // If token received, decode and populate Vuex
      if (data.token) {
        // --- Decode and populate Vuex just like login ---
        const decoded = jwt_decode(data.token);
        localStorage.setItem("token", data.token);

        if (decoded.schoolId) store.commit("SET_SCHOOL_ID", decoded.schoolId);
        if (decoded.role) store.commit("SET_USER_ROLE", decoded.role);
        if (decoded.userId) store.commit("SET_USER_ID", decoded.userId);
        if (decoded.name) store.commit("SET_USER_NAME", decoded.name);

        // Fetch app data
        await store.dispatch("initializeApp");
      }

      isSignedUp.value = true;
      success.value = data?.message || "Account created successfully.";

      // Redirect to onboarding page
      router.replace("/onboarding");
    } catch (err) {
      console.error("Verify OTP Error:", err);

      // Check all possible error sources
      if (err.response?.data) {
        // backend returned something
        error.value =
          err.response.data.message ||
          err.response.data.error ||
          JSON.stringify(err.response.data);
      } else if (err.message) {
        // network or other axios errors
        error.value = err.message;
      } else {
        error.value = "🚨 Verification failed. Please try again.";
      }
    } finally {
      loading.value = false;
    }
  }

  // Local "save and continue" handlers for steps 2-5
  const createdSchool = ref(false);
  const classesAdded = ref(false);
  const teacherAdded = ref(false);
  const studentsAdded = ref(false);

  function isStepDone(id) {
    switch (id) {
      case 1:
        return isSignedUp.value;
      case 2:
        return createdSchool.value;
      case 3:
        return classesAdded.value;
      case 4:
        return teacherAdded.value;
      case 5:
        return studentsAdded.value;
      default:
        return false;
    }
  }
  watch(otp, (value) => {
    if (value.length === 6 && phase.value === "otp" && !loading.value) {
      onVerifyOtp();
    }
  });
</script>

<style scoped>
  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .animate-fade-in {
    animation: fade-in 0.4s ease-in-out;
  }
</style>
